<template>
  <div class="d-flex flex-wrap align-items-start gap-2 pb-3">
    <div class="w-100">
      <LeadSegmentedControl
        v-model="targetingLevel"
        :label="$t('campaigns.facebook.targeting_level_label')"
        :options="targetingLevelOptions" />
    </div>

    <div :class="isCountryLevel ? 'targeting-country-inline' : 'targeting-country'">
      <LeadCombobox
        :items="props.availableCountries"
        :getOptionLabel="(option) => (option as IFacebookCountry).name"
        :getOptionValue="(option) => (option as IFacebookCountry).country_code"
        :modelValue="selectedCountryOption"
        @update:modelValue="(country: IFacebookCountry) => handleUpdate('country', country)"
        :placeholder="$t('campaigns.facebook.choose_country')"
        :error="problemMessage('country')">
        <template #option="{ name, country_code }">
          <div>
            {{ country_code }} <span class="text-muted ms-2">{{ name }}</span>
          </div>
        </template>
      </LeadCombobox>
    </div>

    <LeadButton v-if="isCountryLevel" class="delete-button" variant="outline-danger" @click="emit('remove')">
      <template #icon>
        <IconTrashFilled size="16" />
      </template>
    </LeadButton>

    <template v-if="!isCountryLevel">
      <div class="targeting-location">
        <LeadCombobox
          @search="onSearch"
          :items="locations"
          :getOptionLabel="(option) => (option as IFacebookLocation).name"
          :getOptionValue="(option) => (option as IFacebookLocation).key"
          :modelValue="selectedLocationOption"
          @update:modelValue="(location: IFacebookLocation) => handleUpdate('location', location)"
          :placeholder="$t('campaigns.facebook.choose_location')"
          :loading="locationsLoading"
          :disable-filtering="true"
          :error="problemMessage('location')">
          <template #option="{ label, type }">
            <div>
              {{ label }}<br /><span class="text-muted">{{ type }}</span>
            </div>
          </template>
        </LeadCombobox>
      </div>
      <!-- the tooltip sits on the wrapper, as a disabled input does not reliably report hovering -->
      <div class="targeting-radius" v-tooltip="radiusTooltip">
        <LeadInput
          type="number"
          max="80"
          min="17"
          placeholder="Radius"
          :disabled="radiusDisabled"
          :modelValue="props.value.target_radius"
          @update:modelValue="changeRadius"
          :errors="problemMessage('radius')" />
      </div>
      <LeadButton class="delete-button" variant="outline-danger" @click="emit('remove')">
        <template #icon>
          <IconTrashFilled size="16" />
        </template>
      </LeadButton>
    </template>
  </div>
</template>

<script setup lang="ts">
import { debounce } from '@prospective/lithium';
import type { IFacebookCountry, IFacebookLocation, IFacebookTargeting } from '@/types/locationTargeting';
import { ref, computed, watch } from 'vue';
import { request } from '@/api/client';
import LeadInput from './LeadInput.vue';
import LeadButton from '../generic/LeadButton.vue';
import { IconTrashFilled } from '@tabler/icons-vue';
import LeadCombobox from './LeadCombobox.vue';
import LeadSegmentedControl from './LeadSegmentedControl.vue';
import translator from '@/locale/translator';
import type { FacebookLocationRowProblem } from '@/utils/facebookLocationValidation';

const PROBLEM_MESSAGE_KEYS: Record<FacebookLocationRowProblem, string> = {
  country: 'campaigns.facebook.location_country_required',
  location: 'campaigns.facebook.location_required',
  radius: 'campaigns.facebook.location_radius_range',
};

/* ---------------------------------- STATE --------------------------------- */
const locations = ref<IFacebookLocation[]>([]);
const locationsLoading = ref(false);

/* ---------------------------------- PROPS --------------------------------- */
const props = withDefaults(
  defineProps<{
    value: IFacebookTargeting;
    campaignId: number;
    availableCountries: IFacebookCountry[];
    // cities, regions and places other rows already target
    excludedLocationKeys?: string[];
    /** the field still to fix before the row can be saved; only passed after a refused save */
    problem?: FacebookLocationRowProblem | null;
  }>(),
  {
    excludedLocationKeys: () => [],
    problem: null,
  }
);

/* ---------------------------------- EMITS --------------------------------- */
const emit = defineEmits<{
  (event: 'change', value: IFacebookTargeting): void;
  (event: 'remove'): void;
}>();

/* -------------------------------- COMPUTED -------------------------------- */
const isCountryLevel = computed(() => props.value.target_type === 'country');

const targetingLevelOptions = computed(() => [
  { value: 'country' as const, label: translator.get('campaigns.facebook.targeting_level_country') },
  { value: 'location' as const, label: translator.get('campaigns.facebook.targeting_level_location') },
]);

const targetingLevel = computed({
  get: () => (isCountryLevel.value ? 'country' : 'location') as 'country' | 'location',
  set: (level) => setTargetingLevel(level),
});

const selectedCountryOption = computed(() => {
  return props.availableCountries.find((country) => country.country_code === props.value.target_country) ?? null;
});
const selectedLocationOption = computed(() => {
  return (
    locations.value.find((l) => {
      if (props.value.target_key) {
        return l.key === props.value.target_key;
      }
      return l.name === props.value.target_name;
    }) ?? null
  );
});
const radiusDisabled = computed(() => {
  return !selectedCountryOption.value || props.value.target_type === 'region';
});
// tells why the radius cannot be set, so a disabled box does not look broken
const radiusTooltip = computed(() => {
  if (props.value.target_type === 'region') return translator.get('campaigns.facebook.radius_unavailable_for_region');
  if (radiusDisabled.value) return translator.get('campaigns.facebook.radius_choose_country_first');
  return translator.get('campaigns.facebook.radius_tooltip');
});

/* -------------------------------- WATCHERS -------------------------------- */
// rows are keyed by position, so it listens when a row above is removed
watch(
  () => [props.value.target_key, props.value.target_name, props.value.target_type],
  () => {
    // a row without a saved city/region must not keep the previous row's options
    if (!props.value.target_name || props.value.target_type === 'country') {
      locations.value = [];
      return;
    }
    if (selectedLocationOption.value) return;
    locations.value = [
      {
        key: props.value.target_key,
        name: props.value.target_name,
        label: props.value.target_label || props.value.target_name,
        type: props.value.target_type || 'city',
        country_code: props.value.target_country,
      } as IFacebookLocation,
    ];
  },
  { immediate: true }
);

/* --------------------------------- METHODS -------------------------------- */
function problemMessage(field: FacebookLocationRowProblem): string | null {
  return props.problem === field ? translator.get(PROBLEM_MESSAGE_KEYS[field]) : null;
}

function setTargetingLevel(level: 'country' | 'location') {
  if (level === 'country') {
    const country = selectedCountryOption.value;
    emit('change', {
      ...props.value,
      target_type: 'country',
      target_key: null,
      target_label: country?.name ?? null,
      target_name: country?.name ?? null,
      target_radius: null,
    });
  } else {
    emit('change', {
      ...props.value,
      target_type: null,
      target_key: null,
      target_label: null,
      target_name: null,
      target_radius: null,
    });
  }
}

const handleUpdate = (changeType: 'country' | 'location', data: IFacebookCountry | IFacebookLocation) => {
  if (changeType === 'country' && isCountryLevel.value) {
    emit('change', {
      ...props.value,
      target_country: (data as IFacebookCountry).country_code,
      target_label: (data as IFacebookCountry).name,
      target_name: (data as IFacebookCountry).name,
      target_key: null,
      target_type: 'country',
      target_radius: null,
    });
    locations.value = [];
    return;
  }

  const updates: Partial<IFacebookTargeting> = {
    target_country: data.country_code,
    target_label: null,
    target_name: null,
    target_key: null,
    target_type: null,
  };
  if (changeType === 'location') {
    updates.target_key = data.key;
    updates.target_label = (data as IFacebookLocation).label;
    updates.target_name = (data as IFacebookLocation).name;
    updates.target_type = (
      ['city', 'region'].includes(data.type) ? data.type : 'place'
    ) as IFacebookTargeting['target_type'];

    if (updates.target_type === 'region') updates.target_radius = null;
  } else {
    locations.value = [];
    updates.target_radius = null;
  }

  emit('change', { ...props.value, ...updates });
};

const changeRadius = (newRadius: number | string | null) => {
  // LeadInput turns a cleared number box into 0, which means no radius
  const radius = Number(newRadius) || null;
  emit('change', { ...props.value, target_radius: radius });
};

const searchLocationsDebounced = debounce(searchLocations, 500);

function onSearch(query: string) {
  locationsLoading.value = true;
  searchLocationsDebounced(query);
}

function searchLocations(searchQuery: string) {
  if (!searchQuery) {
    locationsLoading.value = false;
    return;
  }
  request('GET', `/v1/account/campaigns/${props.campaignId}/options/targeting_city`, {
    query: searchQuery,
    country: selectedCountryOption.value?.country_code,
  })
    .then((response: { data: IFacebookLocation[] }) => {
      locations.value = response.data.filter(
        (l) => ['city', 'region', 'place'].includes(l.type) && !props.excludedLocationKeys.includes(l.key)
      );
    })
    .catch((e) => {
      console.error(e);
    })
    .finally(() => {
      locationsLoading.value = false;
    });
}
</script>

<style scoped>
.targeting-country {
  flex: 0 0 100%;
  min-width: 100%;
}

.targeting-country-inline {
  flex: 1;
  min-width: 0;
}

.targeting-location {
  flex: 1;
  min-width: 0;
}

.targeting-radius {
  flex: 0 0 20%;
  min-width: 20%;
}

.targeting-radius :deep(input:disabled) {
  pointer-events: none;
}

.delete-button {
  flex: 0 0 auto;
}
</style>
